/**
 * Aritmética exata sobre as strings decimais do contrato.
 *
 * Módulo **interno**: não é reexportado por `index.ts`, e é de propósito. O que
 * o resto do sistema consome são as funções de negócio que usam estas —
 * `valoresDaProposta`, `valoresDoItemDePedido` —, e não as primitivas. Expor
 * `escalar` publicamente convidaria cada tela a montar a sua própria conta, que
 * é exatamente o que estas funções existem para impedir.
 *
 * `BigInt`, e não `Number`: `0.1 * 3` já erra na segunda casa, e aqui o erro
 * seria centavo gravado em coluna de dinheiro. A `decimal.js` faria o mesmo
 * trabalho, mas ela é dependência de `@construtora/domain`, que o front não
 * carrega — trazê-la para o contrato colocaria a biblioteca no bundle de toda
 * tela do sistema, por causa de algumas multiplicações.
 *
 * Vive num arquivo próprio porque **dois** módulos precisam das mesmas contas: a
 * cotação, que multiplica quantidade ofertada por valor unitário, e o pedido de
 * compra, que faz a mesma conta e ainda soma impostos. Duas cópias divergiriam
 * num arredondamento, e a divergência apareceria como total que muda entre a
 * proposta escolhida e o pedido gerado dela.
 */

export const CASAS_DINHEIRO = 2;
export const CASAS_QUANTIDADE = 4;
/**
 * Casas do **custo unitário** — custo médio e valor unitário de movimento.
 *
 * Seis, e não as duas do dinheiro: custo médio é a taxa que multiplica a
 * quantidade, e o dinheiro é o produto. Espelha `CASAS_CUSTO`, em
 * `@construtora/domain`, que é onde a decisão está explicada — aqui ela existe
 * porque o front não carrega o domínio.
 */
export const CASAS_CUSTO = 6;
/**
 * Casas do **preço unitário de compra** — cotação, pedido, nota, último custo.
 *
 * Seis, e não as duas do dinheiro. A decisão anterior era o contrário, e está
 * registrada em `precoUnitarioSchema`: preço de compra se cobrava em centavos, e
 * seis casas eram coisa de custo médio calculado. Combustível desmentiu a
 * premissa — gasolina e diesel são vendidos com seis casas no litro.
 *
 * Constante própria, e não `CASAS_CUSTO` reaproveitada, pelo mesmo motivo de
 * `CASAS_PERCENTUAL` não ser `CASAS_QUANTIDADE`: são grandezas diferentes que
 * hoje têm a mesma escala, e reusar a constante faria o código dizer que o preço
 * do fornecedor é o custo médio do depósito. No dia em que uma das duas mudar, a
 * outra não vai junto sem que ninguém perceba.
 */
export const CASAS_PRECO = 6;
/**
 * Casas do percentual — índice de rateio, tolerância.
 *
 * São as mesmas quatro da quantidade, e a constante existe assim mesmo: reusar
 * `CASAS_QUANTIDADE` para somar percentuais faria o código dizer que 33,3333% é
 * uma quantidade, e a primeira vez que uma das duas escalas mudasse levaria a
 * outra junto sem que ninguém percebesse.
 */
export const CASAS_PERCENTUAL = 4;
/**
 * Casas de **cada metade do fator de conversão** de unidade (`.spec/015`,
 * decisão 20).
 *
 * Seis, como o preço, e a constante é própria pelo mesmo motivo das outras: fator
 * é a razão entre duas unidades, não dinheiro por unidade, e reusar `CASAS_PRECO`
 * faria o código dizer que o fator KG→SC é um preço.
 *
 * O que a escala **não** limita é a precisão do fator: ele é uma razão, e é por
 * isso que `1000000/22500` guarda exatamente 44,444… peças por m³, que nenhuma
 * escala decimal guardaria. Seis casas são o quanto cada ponta pode ter, não o
 * quanto o quociente vale.
 */
export const CASAS_FATOR = 6;

/** String decimal → inteiro escalado por `10^casas`. O schema já validou o formato. */
export function escalar(valor: string, casas: number): bigint {
  const negativo = valor.startsWith('-');
  const limpo = negativo ? valor.slice(1) : valor;
  const [inteira = '0', fracao = ''] = limpo.split('.');
  const ajustada = `${fracao}${'0'.repeat(casas)}`.slice(0, casas);
  const bruto = BigInt(`${inteira}${ajustada}`);

  return negativo ? -bruto : bruto;
}

/** Inteiro escalado → string decimal com `casas` casas, sempre preenchidas. */
export function desescalar(valor: bigint, casas: number): string {
  const negativo = valor < 0n;
  const digitos = (negativo ? -valor : valor).toString().padStart(casas + 1, '0');
  const corte = digitos.length - casas;

  return `${negativo ? '-' : ''}${digitos.slice(0, corte)}.${digitos.slice(corte)}`;
}

/**
 * Divide arredondando **meio para cima**, que é a regra de dinheiro do projeto
 * (`money`, em `@construtora/domain`). O sinal é tratado sobre o valor absoluto:
 * `Math.round`-como-truncamento arredondaria o negativo para o lado errado.
 */
export function dividirArredondando(valor: bigint, divisor: bigint): bigint {
  const negativo = valor < 0n;
  const absoluto = negativo ? -valor : valor;
  const quociente = absoluto / divisor;
  const resto = absoluto % divisor;
  const arredondado = resto * 2n >= divisor ? quociente + 1n : quociente;

  return negativo ? -arredondado : arredondado;
}

/**
 * `quantidade × unitário`, arredondado a centavo.
 *
 * O corpo das duas funções públicas abaixo. A quantidade entra com 4 casas e o
 * unitário com as suas; o produto sai na soma das duas escalas, e a divisão o
 * traz para os 2 do dinheiro.
 *
 * **Arredondar só no fim**, e não antes de multiplicar: meio metro de cabo a
 * 3,45 viraria 1,72 ou 1,73 conforme a ordem das operações, e a linha de
 * combustível erraria muito mais — `escalar` **trunca**, então um preço de
 * `6.123456` reduzido a 2 casas antes da conta perde R$ 17,28 em 5.000 litros,
 * sempre para menos.
 */
function multiplicarQuantidadePorUnitario(
  quantidade: string,
  unitario: string,
  casasDoUnitario: number,
): bigint {
  const produto = escalar(quantidade, CASAS_QUANTIDADE) * escalar(unitario, casasDoUnitario);

  return dividirArredondando(
    produto,
    10n ** BigInt(CASAS_QUANTIDADE + casasDoUnitario - CASAS_DINHEIRO),
  );
}

/**
 * `quantidade × valorUnitario` de documento de compra, arredondado a centavo.
 *
 * **O unitário entra com 6 casas, e antes entrava com 2.** A escala mudou junto
 * com a coluna — ver `CASAS_PRECO` e `precoUnitarioSchema`. Enquanto eram duas,
 * esta função e a de custo eram diferentes de verdade, e o comentário de lá
 * dizia que passar um custo de `32.815533` por aqui o cortaria em `32.81`. Hoje
 * as duas fazem a mesma conta, e a duplicação some no corpo compartilhado — o
 * que continua distinto são os nomes, porque preço de fornecedor e custo médio
 * de depósito são coisas diferentes que por ora têm a mesma escala.
 */
export function multiplicarQuantidadePorValor(quantidade: string, valorUnitario: string): bigint {
  return multiplicarQuantidadePorUnitario(quantidade, valorUnitario, CASAS_PRECO);
}

/**
 * `quantidade × custoUnitario`, arredondado a centavo.
 *
 * É a mesma conta de `valorDaSaida`, no domínio, feita com a aritmética que o
 * front carrega — o domínio usa `decimal.js`, que o bundle da tela não traz.
 */
export function multiplicarQuantidadePorCusto(quantidade: string, custoUnitario: string): bigint {
  return multiplicarQuantidadePorUnitario(quantidade, custoUnitario, CASAS_CUSTO);
}

/**
 * A fatia de um valor em dinheiro que corresponde a **parte de uma quantidade**.
 *
 * `valor × parte ÷ total`, arredondado a centavo. É a conta que aparece toda vez
 * que um documento leva só um pedaço de outro: a nota que recebe 60 dos 100
 * sacos pedidos precisa do frete, do desconto e da base de imposto daqueles 60.
 *
 * **Igual devolve o valor intacto**, sem passar por arredondamento nenhum. Não é
 * otimização: multiplicar e dividir pelo mesmo número parece inócuo e não é —
 * um valor de 0,015 já gravado voltaria 0,02, e o documento que copia o outro
 * por inteiro passaria a divergir dele em centavos sem que nada tivesse mudado.
 *
 * **Total zerado devolve zero**, e não uma divisão por zero: quantidade original
 * nula não forma proporção, e não há fatia que se possa atribuir.
 *
 * As quantidades entram como strings decimais de 4 casas, e a conta é sobre
 * inteiros escalados: `Number` erraria o centavo em valores grandes, que é
 * exatamente onde a diferença é vista.
 */
export function dinheiroProporcional(valor: string, parte: string, total: string): string {
  const numerador = escalar(parte, CASAS_QUANTIDADE);
  const denominador = escalar(total, CASAS_QUANTIDADE);

  if (denominador === 0n) return desescalar(0n, CASAS_DINHEIRO);
  if (numerador === denominador) return valor;

  return desescalar(
    dividirArredondando(escalar(valor, CASAS_DINHEIRO) * numerador, denominador),
    CASAS_DINHEIRO,
  );
}

/**
 * Quanto `parte` é de `total`, em percentual de 4 casas — o inverso de
 * `dinheiroProporcional(total, indice, '100')`.
 *
 * É o rateio digitado por valor: a fatia de R$ 3.000,00 num título de
 * R$ 10.000,00 é `30.0000`. Total zerado devolve zero, pelo motivo de lá.
 */
export function percentualDoDinheiro(parte: string, total: string): string {
  const denominador = escalar(total, CASAS_DINHEIRO);
  if (denominador === 0n) return desescalar(0n, CASAS_PERCENTUAL);

  return desescalar(
    dividirArredondando(
      escalar(parte, CASAS_DINHEIRO) * 100n * 10n ** BigInt(CASAS_PERCENTUAL),
      denominador,
    ),
    CASAS_PERCENTUAL,
  );
}

/** Soma de strings decimais de dinheiro, como string decimal de 2 casas. */
export function somarDinheiro(valores: readonly string[]): string {
  let total = 0n;
  for (const valor of valores) total += escalar(valor, CASAS_DINHEIRO);

  return desescalar(total, CASAS_DINHEIRO);
}

/** Soma de strings decimais de quantidade, como string decimal de 4 casas. */
export function somarQuantidade(valores: readonly string[]): string {
  let total = 0n;
  for (const valor of valores) total += escalar(valor, CASAS_QUANTIDADE);

  return desescalar(total, CASAS_QUANTIDADE);
}

/** `a − b` sobre quantidades, em 4 casas. Nunca corta abaixo de zero. */
export function subtrairQuantidade(a: string, b: string): string {
  const resultado = escalar(a, CASAS_QUANTIDADE) - escalar(b, CASAS_QUANTIDADE);

  return desescalar(resultado < 0n ? 0n : resultado, CASAS_QUANTIDADE);
}

/** `a − b` sobre dinheiro, em 2 casas. Admite resultado negativo. */
export function subtrairDinheiro(a: string, b: string): string {
  return desescalar(escalar(a, CASAS_DINHEIRO) - escalar(b, CASAS_DINHEIRO), CASAS_DINHEIRO);
}

/** Soma de percentuais, como string decimal de 4 casas. */
export function somarPercentual(valores: readonly string[]): string {
  let total = 0n;
  for (const valor of valores) total += escalar(valor, CASAS_PERCENTUAL);

  return desescalar(total, CASAS_PERCENTUAL);
}

/** `a − b` sobre percentuais, em 4 casas. Admite resultado negativo. */
export function subtrairPercentual(a: string, b: string): string {
  return desescalar(escalar(a, CASAS_PERCENTUAL) - escalar(b, CASAS_PERCENTUAL), CASAS_PERCENTUAL);
}

/**
 * Compara dois percentuais como inteiros escalados: `-1`, `0` ou `1`.
 *
 * Sobre inteiros, e não sobre `Number`: a soma de três índices de 33,3333% mais
 * um de 0,0001% empata em ponto flutuante e passaria por 100% sem ser.
 */
export function compararPercentual(a: string, b: string): -1 | 0 | 1 {
  const x = escalar(a, CASAS_PERCENTUAL);
  const y = escalar(b, CASAS_PERCENTUAL);

  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
}

/** Compara dois valores de dinheiro como inteiros escalados: `-1`, `0` ou `1`. */
export function compararDinheiro(a: string, b: string): -1 | 0 | 1 {
  const x = escalar(a, CASAS_DINHEIRO);
  const y = escalar(b, CASAS_DINHEIRO);

  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
}

/** Compara duas quantidades como inteiros escalados: `-1`, `0` ou `1`. */
export function compararQuantidade(a: string, b: string): -1 | 0 | 1 {
  const x = escalar(a, CASAS_QUANTIDADE);
  const y = escalar(b, CASAS_QUANTIDADE);

  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
}
